import type { ClockSchedule, OwnerReference } from '@felix/domain';
import type { DrillSourceRef } from '../drills/finding-types.js';
import type { ReturnNotice } from '../returned/types.js';
/**
 * PMS ekranlarının veri biçimleri (M7a; ADR 0029). Gemi ve ofis API'leri aynı biçimi döner
 * (`packages/db/src/pms/equipment.ts`).
 */

export type EquipmentNodeType = 'group' | 'system' | 'equipment' | 'component';
export type EquipmentStatus = 'operational' | 'operational_with_restrictions' | 'not_operational';
export type CounterReadingKind = 'reading' | 'meter_replaced' | 'meter_reset';

export interface EquipmentNode {
  id: string;
  parentId: string | null;
  nodeType: EquipmentNodeType;
  code: string;
  name: string;
  /** Equipment Tag No. (ADR 0063 §8). */
  tag?: string | null;
  sfiCode: string | null;
  location: string | null;
  status: EquipmentStatus | string;
  /** Yalnız kendisi kritik işaretli (K22). */
  critical: boolean;
  /** Kendisi ya da atası kritik [NR496 4.4]. */
  effectiveCritical: boolean;
  criticalSourceId: string | null;
  classRelevant: boolean;
  /** Sayaçlı: kartta işaretli ya da okuması olan. */
  hasCounter: boolean;
  outOfPmsManual: boolean;
  sortOrder: number;
  rowVersion: number;
}

/** Bu kişinin bu ekranda yapabilecekleri (yetki ve yazar; plan "Eylem × uygulama"). */
export interface EquipmentActions {
  create: boolean;
  edit: boolean;
  decommission: boolean;
  /** Okuma girişi yalnız gemide (K13). */
  readings: boolean;
}

/**
 * Ofiste geminin kurulum durumu (ADR 0142 §1, §3): `direct` yazarı bulut (kurulum devri),
 * `queued` etkin gemi, kurulumda da canlıda da (ofis yazar, değişiklik gemiye gider; Ek
 * 2026-10-31). Eski sunucuda yok.
 */
export interface SetupSummary {
  state: 'setup' | 'live';
  mode: 'direct' | 'queued';
  /** Gemiye gönderilmiş, sonucu gelmemiş değişiklik. */
  pending: number;
  /** Gemide uygulanamayan değişiklik. */
  rejected: number;
}

/** Kurulumdaki etkin gemide yazma yanıtı (202): değişiklik gemiye gönderildi (ADR 0142 §3). */
export interface QueuedSetupChange {
  queued: true;
  change?: { id: string; status: 'pending' | 'applied' | 'rejected' };
}

export type SetupChangeStatus = 'pending' | 'applied' | 'rejected';
export type SetupChangeTable = 'equipment' | 'job_definitions';
export const SETUP_CHANGE_STATUSES: readonly SetupChangeStatus[] = [
  'pending',
  'applied',
  'rejected',
];
export const SETUP_CHANGE_TABLES: readonly SetupChangeTable[] = ['equipment', 'job_definitions'];

/** Kurulum değişikliği ve gemideki sonucu (ADR 0142 §3; `GET .../pms/setup-changes`). */
export interface SetupChangeItem {
  id: string;
  targetTable: SetupChangeTable;
  op: 'create' | 'update' | 'remove';
  rowId: string;
  code: string | null;
  name: string | null;
  createdAt: string;
  createdBy: string;
  createdByName: string | null;
  /** Ofisin kaldırma gerekçesi. */
  requestReason: string | null;
  status: SetupChangeStatus;
  resultAt: string | null;
  reasonCode: string | null;
  reason: string | null;
}

export interface SetupChangeListView {
  vessel: { id: string; name: string; setupState: 'setup' | 'live'; activated: boolean };
  setup: SetupSummary;
  /** Kişinin görebildiği (değişikliği yapabildiği) modüller. */
  tables: SetupChangeTable[];
  items: SetupChangeItem[];
  total: number;
  page: number;
  pageSize: number;
  counts: Record<SetupChangeStatus, number>;
}

export interface SetupChangeQuery {
  status?: SetupChangeStatus | null;
  table?: SetupChangeTable | null;
  page?: number;
}

/** Kurulum değişiklikleri ekranının yolu (Fleet ▸ Vessels alt sayfası; menüde değil). */
export const SETUP_CHANGES_PATH = '/fleet/setup-changes';
export const setupChangesPath = (vesselId: string): string =>
  `${SETUP_CHANGES_PATH}?vessel=${encodeURIComponent(vesselId)}`;

export function isQueuedChange(value: unknown): value is QueuedSetupChange {
  return (
    typeof value === 'object' && value !== null && (value as { queued?: unknown }).queued === true
  );
}

export interface EquipmentTree {
  vesselId: string;
  /** `cloud`: kurulum devri, ofis hazırlar; `ship`: etkinleşmiş gemi, yalnız gemi yazar (K1). */
  writer: 'cloud' | 'ship';
  /** Ofiste kurulum durumu (ADR 0142); gemide ve eski sunucuda yok. */
  setup?: SetupSummary;
  nodes: EquipmentNode[];
  can: EquipmentActions;
}

export interface Reading {
  id: string;
  equipmentId: string;
  kind: CounterReadingKind;
  readAt: string;
  localDate: string;
  tzOffsetMin: number;
  totalHours: number;
  previousMeterFinal: number | null;
  meterStartHours: number | null;
  cumulativeHours: number;
  reason: string | null;
  source: 'manual' | 'noon_report';
  sourceId: string | null;
  confirmations: string[];
  createdAt: string;
  createdBy: string;
  createdByName: string | null;
}

export interface CounterInfo {
  equipmentId: string;
  equipmentName: string;
  maxDailyHours: number;
  lastReading: Reading | null;
  cumulativeHours: number | null;
  averageDailyHours: number | null;
}

export interface EquipmentTechnical {
  powerKw: number | null;
  rpm: number | null;
  fuelType: string | null;
  cylinders: number | null;
  coolingType: string | null;
  lubricationType: string | null;
  startingSystem: string | null;
}

export interface EquipmentDetail extends EquipmentNode {
  vesselId: string;
  maker: string | null;
  model: string | null;
  serialNo: string | null;
  installedOn: string | null;
  criticalReason: string | null;
  ismRelevant: boolean;
  safetyCritical: boolean;
  outOfPmsReason: string | null;
  bvItemCode: string | null;
  surveyPrinciple: string | null;
  hasCounterFlag: boolean;
  maxDailyHours: number;
  noonReportSource: string | null;
  technical: Partial<EquipmentTechnical> | null;
  notes: string | null;
  path: { id: string; name: string; nodeType: EquipmentNodeType }[];
  childCount: number;
  counter: CounterInfo | null;
  deletedAt: string | null;
  decommissionReason: string | null;
  createdAt: string;
  updatedAt: string;
  /** Kurulum devrinde ofiste hazırlandı (K37). */
  preparedInOffice: boolean;
}

/** Equipment List üst kartı: gemi özellikleri ve Next Port. */
export interface VesselParticulars {
  id: string;
  name: string;
  imo: string | null;
  timeZone: string;
  /** Gemi saati çizelgesi (ADR 0065 §4); eski sunucu göndermez. */
  clock?: ClockSchedule;
  vesselType: string | null;
  builtYear: number | null;
  flag: string | null;
  classSociety: string | null;
  classNo: string | null;
  callSign: string | null;
  /** Risk ana verisinin gemi grubu (ADR 0086 §4); eski sunucu göndermez. */
  raGroup?: string | null;
  /** Charter'sız seferin varsayılan Owner's reference'ı (ADR 0084 Ek 2); eski sunucu göndermez. */
  ownerReference?: OwnerReference | null;
  /** Gross tonnage, ondalık metin (ADR 0117 §8); eski sunucu göndermez. */
  grossTonnage?: string | null;
  /** ISM sorumlusu şirket (ADR 0117 §8); eski sunucu göndermez. */
  ismCompany?: string | null;
  nextPort: string | null;
  writer: 'cloud' | 'ship';
  rowVersion: number;
  /**
   * Ofiste: uygulanan doküman şablonu ve sürümü, salt okunur (ADR 0037 §4); şablonsuz eski gemide
   * `null`. Gemide yok.
   */
  docTemplate?: { id: string; name: string | null; version: number } | null;
}

export interface VesselParticularsInput {
  vesselType: string | null;
  builtYear: number | null;
  flag: string | null;
  classSociety: string | null;
  classNo: string | null;
  callSign: string | null;
  /** Risk ana verisinin gemi grubu (ADR 0086 §4). */
  raGroup?: string | null;
  /** Owner's reference (ADR 0084 Ek 2); hepsi boşsa `null`. */
  ownerReference?: OwnerReference | null;
  /** Gross tonnage (ADR 0117 §8), ondalık metin. */
  grossTonnage?: string | null;
  /** ISM sorumlusu şirket (ADR 0117 §8). */
  ismCompany?: string | null;
}

/** Ekipman kartı formu (PMS belgesi §4–§5). */
export interface EquipmentInput {
  nodeType: EquipmentNodeType;
  code: string;
  name: string;
  tag?: string | null;
  sfiCode: string | null;
  maker: string | null;
  model: string | null;
  serialNo: string | null;
  installedOn: string | null;
  location: string | null;
  status: EquipmentStatus;
  critical: boolean;
  criticalReason: string | null;
  classRelevant: boolean;
  ismRelevant: boolean;
  safetyCritical: boolean;
  outOfPmsManual: boolean;
  outOfPmsReason: string | null;
  bvItemCode: string | null;
  surveyPrinciple: string | null;
  hasCounter: boolean;
  maxDailyHours: number;
  noonReportSource: string | null;
  technical: Partial<EquipmentTechnical> | null;
  notes: string | null;
}

export interface ReadingInput {
  kind: CounterReadingKind;
  readAt: string;
  totalHours: number;
  previousMeterFinal?: number | null;
  meterStartHours?: number | null;
  reason?: string | null;
  confirmations: string[];
  source?: 'manual' | 'noon_report';
  sourceId?: string | null;
}

export interface CounterSuggestion {
  hours: number;
  reportId: string;
  reportedAt: string;
}

/* ---------- İşler, plan, iş emirleri ve geçmiş (M7b3; `packages/db/src/pms/views.ts`) ---------- */

export type CalendarUnit = 'day' | 'week' | 'month' | 'year';

export interface JobIntervalView {
  calendar: { every: number; unit: CalendarUnit } | null;
  runningHours: number | null;
  conditionPointIds: string[];
}

export interface ChecklistItem {
  text: string;
  kind: 'check' | 'value';
  unit: string | null;
}

/** Görev kütüphanesindeki iş (PMS belgesi §7–§10). */
export interface JobView {
  id: string;
  rowVersion: number;
  vesselId: string;
  equipmentId: string;
  active: boolean;
  changeReason: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  deletedAt: string | null;
  code: string;
  title: string;
  workType: string;
  jobType: string;
  priority: string;
  interval: JobIntervalView;
  intervalBasis: string;
  counterEquipmentId: string | null;
  procedure: string[] | null;
  checklist: ChecklistItem[] | null;
  safetyItems: string[] | null;
  acceptanceCriteria: string | null;
  requirementRefs: { source: string; reference: string }[] | null;
  responsibleRoleId: string | null;
  estimatedMinutes: number | null;
  instructionDocumentId: string | null;
  manualDocumentId: string | null;
  manualRef: string | null;
  plannedSpareParts: { materialId: string; quantity: number }[] | null;
  ceSurvey: boolean | null;
  /** UMS / E0 alarm testi işi (ADR 0133 §3); eski sunucuda yok. */
  alarmTest?: boolean;
  baselineDoneOn: string | null;
  baselineDoneHours: number | null;
}

export interface JobList {
  items: JobView[];
  /** Ofiste: `cloud` kurulum devri (ofis düzeltir), `ship` etkin gemi (salt okunur; K1). */
  writer?: 'cloud' | 'ship';
  /** Ofiste kurulum durumu (ADR 0142); gemide ve eski sunucuda yok. */
  setup?: SetupSummary;
  can: { create: boolean; edit: boolean; delete: boolean; intervalChange?: boolean };
}

/** İş tanımı girdisi: domain şemasının biçimi (`jobDefinitionInputSchema`). */
export type JobInput = Omit<
  JobView,
  | 'id'
  | 'rowVersion'
  | 'vesselId'
  | 'equipmentId'
  | 'active'
  | 'changeReason'
  | 'createdAt'
  | 'createdBy'
  | 'updatedAt'
  | 'deletedAt'
>;

export type PlanStatus = 'on_time' | 'due_soon' | 'overdue' | 'postponed';

export interface PlanDue {
  calendarDueDate?: string;
  dueHours?: number;
  estimatedHoursDueDate?: string;
  dueOn?: string;
}

/** Planın bir işi: vade ve durum türetilir (K39). */
export interface PlanItem {
  jobId: string;
  equipmentId: string;
  code: string;
  title: string;
  priority: string;
  jobType: string;
  workType: string;
  equipmentCode: string | null;
  equipmentName: string | null;
  responsibleRoleId: string | null;
  responsibleRoleName: string | null;
  lastDone: {
    source: 'record' | 'baseline' | 'start';
    recordId: string | null;
    doneOn: string;
    runningHours: number | null;
    pendingApproval: boolean;
  };
  effectiveInterval: { calendar?: { every: number; unit: CalendarUnit }; runningHours?: number };
  intervalChangePending: boolean;
  firstOriginalDue: PlanDue;
  due: PlanDue;
  postponed: boolean;
  postponementPending: boolean;
  status: PlanStatus;
  critical: boolean;
  criticalOverdue: boolean;
  hoursBaselineMissing?: boolean;
  currentHours?: number;
  remainingHours?: number;
  remainingDays?: number;
  overdueDays?: number;
}

export interface PlanSummary {
  total: number;
  today: number;
  overdue: number;
  dueNext7Days: number;
  dueNext30Days: number;
  criticalOverdue: number;
}

export interface PlanView {
  today: string;
  plans: PlanItem[];
  summary: PlanSummary;
}

export type ApprovalStatus = 'draft' | 'pending' | 'approved' | 'returned';

export interface WorkOrderRecord {
  /** İade ya da ret bandı (ADR 0116 Ek); eski sunucuda yok. */
  returnNotice?: ReturnNotice | null;
  id: string;
  contentHash: string;
  doneOn: string;
  status: ApprovalStatus;
  submissionId: string | null;
  step: number | null;
  office: boolean;
}

export interface WorkOrderView {
  /** Kaynak tatbikat bulgusu (ADR 0128 Faz 4 Ek); eski sunucuda yok. */
  drillSource?: DrillSourceRef | null;
  id: string;
  vesselId: string;
  woNo: string;
  kind: string;
  /** `planned`, `in_progress`, `cancelled`; raporlanmışsa durum kayıttan türetilir. */
  status: string;
  jobId: string | null;
  jobCode: string | null;
  workType: string | null;
  equipmentId: string;
  equipmentCode: string | null;
  equipmentName: string | null;
  title: string;
  priority: string;
  targetOn: string | null;
  assignedTo: string | null;
  assignedToName: string | null;
  startedAt: string | null;
  safetyCheck: { items?: { index: number; checked: boolean }[] } | null;
  cancelReason: string | null;
  rowVersion: number;
  createdAt: string;
  createdBy: string;
  due: {
    kind: string;
    targetOn: string | null;
    dueOn: string | null;
    calendarDueDate: string | null;
    dueHours: number | null;
    postponed: boolean;
    postponementPending: boolean;
    reported: boolean;
    status: PlanStatus | null;
  };
  instruction: {
    revisionId: string | null;
    sha256: string | null;
    missing: boolean;
    supersededSinceOpen: boolean;
  };
  record: WorkOrderRecord | null;
}

export interface WorkOrderList {
  items: WorkOrderView[];
  can?: { create: boolean; edit: boolean; report: boolean };
}

export type HistoryStatus = 'pending' | 'approved' | 'returned' | 'voided';

export interface ChecklistAnswer {
  index: number;
  answer: 'pass' | 'fail' | 'na';
  value: number | null;
  remark: string | null;
}

/** History: raporlu iş kaydı; iptal edilen "VOID" ve gerekçesiyle (K14) [NR496 4.3.2a]. */
export interface HistoryEntry {
  id: string;
  workOrderId: string;
  woNo: string | null;
  kind: string | null;
  jobId: string | null;
  jobCode: string | null;
  jobTitle: string | null;
  jobDeleted: boolean;
  equipmentId: string;
  equipmentCode: string | null;
  equipmentName: string | null;
  equipmentDeleted: boolean;
  doneOn: string;
  doneAt: string;
  tzOffsetMin: number;
  meterHours: number | null;
  runningHours: number | null;
  result: string;
  conditionAfter: string | null;
  performedBy: { userId: string | null; name: string }[] | null;
  workPerformed: string;
  remarks: string | null;
  /** Gecikme gerekçesi [NR496 4.5.4b, 4.6.2a] (ADR 0029 Ek M7f F1). */
  overdueReason?: string | null;
  /** CM raporu sağlayıcıdan bekleniyor [NR496 4.5.4e] (F4). */
  cmReportPending?: boolean;
  /** Bakım öncesi durum ve gerçek adam-saat (ADR 0132 §2): isteğe bağlı. */
  conditionBefore?: string | null;
  workHours?: number | null;
  sparePartsUsed: { materialId: string; quantity: number }[] | null;
  checklistResult?: ChecklistAnswer[] | null;
  dueOnAtCompletion: string | null;
  dueHoursAtCompletion: number | null;
  firstOriginalDueOn: string | null;
  firstOriginalDueHours: number | null;
  supersedesRecordId: string | null;
  contentHash: string;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
  status: HistoryStatus;
  void: { reason: string; at: string; by: string; afterReturn: boolean } | null;
}

export interface HistoryFilter {
  equipment?: string;
  job?: string;
  from?: string;
  to?: string;
}

/** Karar bekleyen PMS kaydı (zil ve ofis listesi): bekleyen adım, içerik ve hash. */
export interface PendingItem {
  recordType: string;
  recordId: string;
  vesselId: string;
  submissionId: string;
  step: number;
  office: boolean;
  approverRoleId: string | null;
  contentHash: string;
  since: string | null;
  jobId: string | null;
  jobCode: string | null;
  jobTitle: string | null;
  workOrderId: string | null;
  equipmentName: string | null;
  /** Arıza ve breakdown kapanışında arıza numarası (M7c). */
  defectNo?: string | null;
  content: Record<string, unknown>;
}

/** Ek (K15): ofiste `officeState` dosyanın aktarım durumudur. */
export interface PmsAttachment {
  id: string;
  ownerType: string;
  ownerId: string;
  kind: string;
  fileName: string;
  size: number;
  mimeType: string;
  sha256: string;
  caption: string | null;
  createdAt: string;
  createdBy: string;
  voided: boolean;
  upload: string | null;
  officeState?: 'verified' | 'uploading' | 'on_vessel';
}

/** Karar: içerik hash'i, görülen gönderim ve adım; parola yeniden (mimari belge §7). */
export interface DecisionInput {
  contentHash: string;
  submissionId: string;
  step: number;
  decision: 'approved' | 'returned';
  reason?: string;
  /** Yalnız girildiyse (ADR 0075): şirket ayarı açıkken ya da kullanıcı verdiyse. */
  password?: string;
}

/** Complete & Report formu (PMS belgesi §13). */
export interface WorkRecordForm {
  doneAt: string;
  tzOffsetMin?: number;
  meterHours: number | null;
  performedBy: { userId: string | null; name: string }[];
  workPerformed: string;
  checklistResult: ChecklistAnswer[];
  result: 'pass' | 'fail';
  conditionAfter: string | null;
  sparePartsUsed: { materialId: string; quantity: number }[];
  remarks: string | null;
  /** Yalnız doluyken (ADR 0029 Ek M7f F1, F4). */
  overdueReason?: string | null;
  cmReportPending?: boolean;
  /** Yalnız doluyken (ADR 0132 §2). */
  conditionBefore?: string | null;
  workHours?: number | null;
}

export interface PostponementInput {
  jobId: string;
  workOrderId?: string | null;
  newDueOn: string | null;
  newDueHours: number | null;
  reason: string;
  classReference?: string | null;
  /** Yalnız girildiyse (ADR 0075). */
  password?: string;
}

/** Ertelemenin durumu (ADR 0108 §4): `revoked` ofis kararıyla geri çevrildi. */
export type PostponementStatus = 'pending' | 'approved' | 'returned' | 'revoked';

export type PostponementActionKind = 'comment' | 'revoke' | 'change_due';

/** Ofisin ertelemeye notu ya da kararı ve gemideki sonucu (ADR 0108 §3). */
export interface PostponementOfficeEntryView {
  id: string;
  kind: PostponementActionKind;
  text: string;
  newDueOn: string | null;
  newDueHours: number | null;
  classReference: string | null;
  seenDueOn: string | null;
  seenDueHours: number | null;
  byName: string | null;
  at: string;
  /** Kararın gemideki sonucu; yorumda ve gemiye ulaşmamış kararda `null`. */
  result: {
    outcome: 'applied' | 'rejected';
    code: string | null;
    reason: string | null;
    at: string;
  } | null;
}

/** Ertelemeler listesinin satırı (ADR 0108 §4; iki uygulamada aynı). */
export interface PostponementView {
  /** İade ya da ret bandı (ADR 0116 Ek); eski sunucuda yok. */
  returnNotice?: ReturnNotice | null;
  id: string;
  vesselId: string;
  vesselName: string | null;
  jobId: string;
  jobCode: string | null;
  jobTitle: string | null;
  equipmentId: string | null;
  equipmentCode: string | null;
  equipmentName: string | null;
  workOrderId: string | null;
  workOrderNo: string | null;
  reason: string;
  classItem: boolean;
  classReference: string | null;
  requestedByName: string | null;
  requestedAt: string;
  approvedByName: string | null;
  approvedAt: string | null;
  returnReason: string | null;
  status: PostponementStatus;
  /** İşin vadesini bugün bu erteleme belirliyor mu. */
  active: boolean;
  /**
   * Onaylı erteleme iş yapılınca sona erdi (ortak 2026-10-25): işin döngüsü sonraki kayıtla
   * değişti, vade artık o kayıttan. Eski sunucuda yok.
   */
  superseded?: boolean;
  originalDueOn: string | null;
  originalDueHours: number | null;
  requestedDueOn: string | null;
  requestedDueHours: number | null;
  newDueOn: string | null;
  newDueHours: number | null;
  delayDays: number | null;
  delayHours: number | null;
  officeChanged: boolean;
  office: PostponementOfficeEntryView[];
  /** Ofiste: kişi not ve karar yazabilir mi (`postponements.approve`). */
  canAct?: boolean;
}

/** Ofisin notu ya da kararı (ADR 0108 §3). */
export interface PostponementActionInput {
  kind: PostponementActionKind;
  text: string;
  newDueOn: string | null;
  newDueHours: number | null;
  classReference: string | null;
  /** Yalnız girildiyse (ADR 0075). */
  password?: string;
}

/**
 * Aralık geçmişinin satırı (ADR 0104 §7): gemide yazılmış değişiklik (kaynak Ship ya da Office)
 * ya da gemide henüz uygulanmamış / uygulanamamış ofis kararı.
 */
/** İşin değişiklik kaydı satırı (DNV-CP-0206 Sec.2 [4.6] b; ADR 0133 §2). */
export interface JobChangeEntry {
  at: string;
  userId: string;
  userName: string | null;
  role: string | null;
  roleSource: 'vessel' | 'company' | null;
  kind: 'created' | 'interval' | 'instruction' | 'interval_instruction';
  reason: string | null;
  fields: { field: string; oldValue: unknown; newValue: unknown }[];
}

export interface IntervalHistoryEntry {
  /** Değişikliği yapanın o günkü rolü (ADR 0133 §2); eski sunucuda yok. */
  byRole?: string | null;
  id: string;
  kind: 'change' | 'office_decision';
  source: 'ship' | 'office';
  at: string;
  byName: string | null;
  previousInterval: JobIntervalView | null;
  newInterval: JobIntervalView;
  direction: 'extend' | 'shorten' | null;
  intervalBasis: string;
  reason: string;
  classReference: string | null;
  status: 'effective' | 'pending' | 'returned' | 'sent' | 'rejected';
  officeDecisionId: string | null;
  decidedAt: string | null;
  rejection: { code: string; reason: string | null } | null;
}

export interface IntervalChangeInput {
  interval: JobIntervalView;
  intervalBasis: string;
  reason: string;
  classReference?: string | null;
  /** Yalnız girildiyse (ADR 0075). */
  password?: string;
}

/** İş emri açma: planlı (işten) ya da plansız (ekipman ve başlık). */
export type OpenWorkOrderInput =
  | { jobId: string; assignedTo?: string | null }
  | {
      kind: 'unplanned' | 'corrective';
      equipmentId: string;
      title: string;
      priority?: string;
      targetOn?: string | null;
      assignedTo?: string | null;
    };

/** Talimat seçicisinin dokümanı (K38): yalnız yayındaki `pms_instructions`. */
export interface InstructionOption {
  id: string;
  docNo: string;
  title: string;
}

export interface SparePartOption {
  materialId: string;
  name: string;
  /** Seçicinin etiketi: ad, özellik ve kod; arama bu metinde yapılır (ADR 0122 §4). */
  label: string;
  /** Şirket kodu, yoksa IMPA. */
  code: string | null;
  unit: string;
  onHand: number | null;
}

export interface Assignee {
  id: string;
  name: string;
  role: string | null;
}

/** Dolaşan bileşen (DNV-CP-0206 Sec.2 [4.3] c; ADR 0133 §6); `@felix/db` `RotableView` kopyası. */
export interface RotableMovementView {
  id: string;
  kind: 'install' | 'remove';
  equipmentId: string;
  equipmentName: string | null;
  movedOn: string;
  positionHours: number | null;
  reason: string | null;
  byName: string | null;
  createdAt: string;
}

export interface RotablePeriodView {
  equipmentId: string;
  equipmentName: string | null;
  installedOn: string;
  removedOn: string | null;
  installHours: number | null;
  removeHours: number | null;
  hours: number | null;
}

export interface RotableView {
  id: string;
  rowVersion: number;
  serialNo: string;
  name: string;
  partNo: string | null;
  maker: string | null;
  initialHours: number;
  registeredOn: string;
  notes: string | null;
  equipmentId: string | null;
  equipmentName: string | null;
  installedOn: string | null;
  totalHours: number;
  hoursIncomplete: boolean;
  periods: RotablePeriodView[];
  movements: RotableMovementView[];
  deletedAt: string | null;
  decommissionReason: string | null;
}

export interface RotableList {
  items: RotableView[];
  can: { create: boolean; move: boolean; decommission: boolean };
}

export interface RotableInputValues {
  serialNo: string;
  name: string;
  partNo: string | null;
  maker: string | null;
  initialHours: number;
  registeredOn: string;
  notes: string | null;
}

export interface RotableMovementValues {
  kind: 'install' | 'remove';
  equipmentId: string;
  movedOn: string;
  positionHours: number | null;
  reason: string | null;
}

/** Yalnız gemide (K13). */
export interface RotableEditor {
  register(values: RotableInputValues): Promise<RotableView>;
  move(id: string, values: RotableMovementValues): Promise<RotableView>;
  decommission(id: string, rowVersion: number, reason: string): Promise<RotableView>;
}
